import { motion } from "framer-motion";
import { Check, ExternalLink } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const PROOF = [
  "AS9100D & ISO 9001:2015 Certified Quality Management System",
  "CMM & Vision Inspection",
  "First Article Inspection Reporting",
  "Digital Product Definition",
];

// H — Quality & verification.
export const Quality = () => (
  <section id="quality" data-testid="quality-section" className="on-light bg-[var(--cream)] py-24 sm:py-32">
    <div className="wrap grid items-center gap-14 lg:grid-cols-[1fr_1.05fr] lg:gap-20">
      <Reveal>
        <div className="relative overflow-hidden rounded-[0.75rem] border border-[var(--line)]">
          <img
            src="https://images.unsplash.com/photo-1585366958113-e28e8e580d3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Coordinate measurement probe inspecting a machined ring component (illustrative imagery)"
            loading="lazy"
            className="h-[22rem] w-full object-cover sm:h-[30rem]"
          />
          <div className="absolute left-4 top-4 rounded-full bg-[#0b0e13]/80 px-3.5 py-1.5 font-mono text-[9.5px] tracking-[0.2em] text-white/90 backdrop-blur">
            ILLUSTRATIVE IMAGERY
          </div>
          <motion.span className="pointer-events-none absolute inset-x-8 top-1/2 h-px bg-[#2f7bff]/70" />
        </div>
      </Reveal>

      <div>
        <SectionHead
          no="05"
          eyebrow="QUALITY & VERIFICATION"
          title={<>A precise part. <span className="font-serif-i">A clearer basis for confidence.</span></>}
          intro="Dimensions matter. So does how they are checked."
        />
        <Reveal delay={0.2}>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--grey)] sm:text-lg [text-wrap:pretty]">
            PMP’s quality capabilities include coordinate measurement, vision inspection, first article inspection reporting, and digital product definition processes.
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--grey)] sm:text-lg [text-wrap:pretty]">
            Bring your inspection and documentation requirements into the discussion before manufacturing begins.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {PROOF.map((p, i) => (
            <Reveal key={p} delay={0.22 + i * 0.06}>
              <div className="flex h-full items-start gap-3 rounded-xl border border-[var(--line)] bg-white px-5 py-4">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--blue)]">
                  <Check className="h-3 w-3 text-white" />
                </span>
                <span className="text-[14.5px] font-medium leading-snug text-[var(--ink)]">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.4}>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="https://www.pmpmach.com/qualityd776c0ba"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quality-explore-button"
              className="btn btn-dark"
            >
              Explore Quality Capabilities <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              href="https://www.pmpmach.com/qualityd776c0ba"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quality-certifications-link"
              className="font-mono text-[11px] tracking-[0.18em] text-[var(--ink-soft)] underline decoration-[var(--blue)] decoration-2 underline-offset-4 transition-colors hover:text-[var(--blue)]"
            >
              VIEW CERTIFICATIONS
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

